import { AdmStack, CreateStack, isAdmStack, isPbsStack, PbsStack, Stack } from '@pubstack/common/src/stack'
import { useQueryClient } from '@tanstack/react-query'
import { FunctionComponent, useCallback } from 'react'
import { STACKS_QUERY_KEYS, toBulkUpdate, useBulkUpdateStacks, useCreateStack } from '~/api/stacks.api'
import Button from '~/components/Button'
import { useGlobalModal } from '~/components/layout/GlobalModal'
import { Modal } from '~/components/Modal'
import { PbsStackChangelog } from './PbsStackChangelog'
import { AdmStackChangelog } from './AdmStackChangelog'
import { useAuthenticatedUser } from '~/auth/user.hooks'
import { Link } from '~/components/Link'
import { useProduct } from '~/hooks/useProduct'
import { getEnvFromUrl } from '~/utils/environment'
import { STACKS_COMPARATOR_URL } from '~/utils/constants'

type Props = {
  stack: AdmStack | PbsStack
  /**
   * The other version of the group on screen, if the group has one. It may be the live one this version
   * replaces — but so may `stack` itself, which is the usual case, so neither alone answers "what is live
   * here": `handleCreateAndDeploy` reads the status of both.
   */
  concurrentStack?: Stack
}

export const CreateUpdatedVersionModal: FunctionComponent<Props> = ({ stack, concurrentStack }) => {
  // API Hooks
  const { mutateAsync: createStack, isPending: isCreatePending } = useCreateStack()
  const { mutateAsync: bulkUpdateStacks, isPending: isDeployPending } = useBulkUpdateStacks({
    successToast: (
      <>
        <strong>{stack.name}</strong> is <strong>Live</strong>
      </>
    ),
    errorToast: 'An error occurred while trying to deploy the stack.',
  })
  const queryClient = useQueryClient()
  const { isAdmin } = useAuthenticatedUser()
  const { product } = useProduct()

  // Hooks
  const { close } = useGlobalModal()

  // Variables
  const env = getEnvFromUrl(window.location.host)
  // Both buttons create, so neither stays live while the other is running.
  const isBusy = isCreatePending || isDeployPending

  const buildNewStack = useCallback(
    (): CreateStack =>
      ({
        type: stack.type,
        siteId: stack.siteId,
        name: stack.name,
        configuration: stack.configuration,
        stackGroupId: stack.stackGroupId,
        priority: stack.priority,
      }) as CreateStack,
    [stack]
  )

  const handleCreateUpdatedVersion = useCallback(async () => {
    // The backend auto-archives existing to-deploy stacks in the same group on create
    await createStack(buildNewStack())

    close()
  }, [createStack, buildNewStack, close])

  /**
   * Synchronize and deploy in one step, at the full 100%.
   *
   * The two-step route — create the version, close, then find the row again and deploy it — is the one worth
   * keeping as the primary: an updated version is normally read before it is served. This is for the case
   * where the change is already understood and splitting it over two dialogs only adds a place to stop.
   *
   * Deploys at ratio 1 rather than offering the A/B dial: a button that both synchronises and asks how much
   * traffic to send would be two decisions on one control. Anything but 100% goes through Deploy, which is
   * what that dialog is for.
   *
   * Every live version of the group is archived, `stack` included — not just `concurrentStack`. The row this
   * dialog opens from is the last of its group, and once a group has been deployed the archived predecessors
   * drop out of the list, so that row is usually the live version itself and its group holds nothing else.
   * Archiving only the sibling therefore archived nothing at all in the ordinary "it drifted, re-sync it"
   * case, and the group came out of the deploy serving two live versions at ratio 1 apiece. Same rule the
   * Deployment Center applies to a group it deploys: whatever was live stops being live.
   */
  const handleCreateAndDeploy = useCallback(async () => {
    const created = await createStack(buildNewStack())
    const toUpdate = [toBulkUpdate({ ...created, status: 'live', ratio: 1 })]
    // A/B is not reachable here — the button is disabled on any live row below ratio 1 — so a group with two
    // live versions is already the corrupted state, and archiving both is the way out of it rather than a
    // second bug.
    const liveStacks = [stack as Stack, concurrentStack].filter((candidate) => candidate?.status === 'live') as Stack[]
    for (const liveStack of liveStacks) {
      toUpdate.push(toBulkUpdate({ ...liveStack, status: 'archived', ratio: 0 }))
    }
    await bulkUpdateStacks(toUpdate)
    queryClient.invalidateQueries({ queryKey: [...STACKS_QUERY_KEYS, { siteId: stack.siteId }] })

    close()
  }, [createStack, buildNewStack, bulkUpdateStacks, stack, concurrentStack, queryClient, close])

  return (
    <Modal.Content>
      <Modal.Title>Synchronize</Modal.Title>

      <Modal.Body>
        <div>
          You are about to synchronize <strong>{stack?.name}.</strong>
        </div>
        <br />
        <div>The following elements will be synchronized:</div>
        {isPbsStack(stack) && <PbsStackChangelog output1={stack.output} output2={stack.snapshot} />}
        {isAdmStack(stack) && <AdmStackChangelog output1={stack.output} output2={stack.snapshot} />}
        {isAdmin && (
          <>
            <br />
            <Link
              icon={'open_in_new'}
              target={'_blank'}
              rel={'noreferrer'}
              label={'Stacks comparator'}
              trackingLabel={`${product}-stacks-comparator-navigate`}
              href={`${STACKS_COMPARATOR_URL}/?scope=${stack.scopeId}&site=${stack.siteId}&ref=${stack.stackGroupId}&env=${env === 'preview' || env === 'local' ? 'dev' : env}`}
            />
          </>
        )}
      </Modal.Body>
      <Modal.Actions>
        <Button variant="tertiary" onClick={close} trackingLabel={`${stack.type}-sites-stacks-synchronize-cancel`}>
          Cancel
        </Button>
        <Button
          variant="secondary"
          iconName={isDeployPending ? 'pubstack' : undefined}
          iconSpin={isDeployPending}
          onClick={handleCreateAndDeploy}
          disabled={isBusy}
          // `apply` — the tracking vocabulary's word for activating an effect — rather than a verb of this
          // dialog's own: deploying at 100% is that, and the closed list is what keeps the funnels comparable.
          trackingLabel={`${stack.type}-sites-stacks-synchronize-apply`}
        >
          Update and deploy at 100%
        </Button>
        <Button
          variant="primary"
          iconName={isCreatePending ? 'pubstack' : undefined}
          iconSpin={isCreatePending}
          onClick={handleCreateUpdatedVersion}
          disabled={isBusy}
          trackingLabel={`${stack.type}-sites-stacks-synchronize-confirm`}
        >
          Create updated version
        </Button>
      </Modal.Actions>
    </Modal.Content>
  )
}
